import React, { useState, useMemo, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
} from "date-fns";
import { useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { FaDownload } from "react-icons/fa";
import {
  getDoorStepEmployeeAttendanceDetailsApi,
  getDoorStepEmployeeAttendanceDetailsExportApi,
} from "../../../services/DoorStepEmployeeService";
import GlobalSearchableDropdown from "../../Constant/SearchableDropdown";
import { CustomTooltip } from "../../../components/CustomTooltip";
import { getMonthOptions, getYearsOptions } from "../../../utilities";
const statusColors = {
  PRESENT: "text-bg-success-soft border border-success",
  ABSENT: "text-bg-danger-soft border border-danger",
  HOLIDAY: "text-bg-warning-soft border border-warning",
  EARNED_LEAVE: "text-bg-info-soft border border-info",
  PAID_LEAVE: "text-bg-success-soft border border-success",
  UNPAID_LEAVE: "text-bg-warning-soft border border-warning",
};

export default function DoorStepEmployeeAttendanceDetails() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedYear, setSelectedYear] = useState(getYearsOptions()[0]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const getAttendance = () => {
    setLoading(true);
    getDoorStepEmployeeAttendanceDetailsApi(id, {
      year: selectedYear?.value,
      month: selectedMonth?.value,
    })
      .then((res) => {
        setError("");
        setAttendanceData(res?.data?.results || []);
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedYear) {
      const months = getMonthOptions(selectedYear.value);
      setSelectedMonth(months[months.length - 1]);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (selectedYear && selectedMonth) {
      getAttendance();
    }
  }, [selectedMonth]);

  const getMonthDays = (year, month) => {
    if (!year || !month) return { daysInMonth: [], emptyCells: [] };

    const start = startOfMonth(new Date(year, month - 1));
    const end = endOfMonth(new Date(year, month - 1));

    const daysInMonth = eachDayOfInterval({ start, end });
    const firstDayOfMonth = getDay(start);
    const emptyCells = Array(firstDayOfMonth).fill(null);

    return { daysInMonth, emptyCells };
  };

  const { daysInMonth, emptyCells } = getMonthDays(
    selectedYear?.value,
    selectedMonth?.value
  );

  const getStatus = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return attendanceData.find((a) => a.date === dateStr)?.status;
  };

  const statusCounts = useMemo(() => {
    const counts = {};
    attendanceData.forEach((a) => {
      counts[a.status] = (counts[a.status] || 0) + 1;
    });
    return counts;
  }, [attendanceData]);
  const handleExport = () => {
    setExportLoading(true);
    getDoorStepEmployeeAttendanceDetailsExportApi(id, {
      year: selectedYear?.value,
      month: selectedMonth?.value,
      export: true,
    })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "DoorStep-Employee-Attendance-report.xlsx";

        // Append to DOM to make it more consistent
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setExportLoading(false);
      });
  };
  if (loading) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger mt-3">{error}</div>;
  }
  return (
    <div className="container-fluid p-4">
      <div className="row g-3">
        <div className="col-xs-12 col-md-3">
          <h6 className="fw-bold mb-3">Status Overview</h6>
          <div className="d-flex flex-row flex-md-column gap-2 flex-wrap ">
            <div className="d-flex justify-content-between align-items-center p-2 rounded-2 flex-fill flex-md-grow-0 text-bg-success-soft border border-success">
              <span className="fw-medium text-darker">Present</span>
              <span className="rounded-1 badge bg-success text-white">
                {statusCounts?.PRESENT || 0}
              </span>
            </div>

            <div className="d-flex justify-content-between align-items-center p-2 rounded-2 flex-fill flex-md-grow-0 text-bg-danger-soft border border-danger">
              <span className="fw-medium text-darker">Absent</span>
              <span className="rounded-1 badge bg-danger text-white">
                {statusCounts?.ABSENT || 0}
              </span>
            </div>

            <div className="d-flex justify-content-between align-items-center p-2 rounded-2 flex-fill flex-md-grow-0 text-bg-warning-soft border border-warning">
              <span className="fw-medium text-darker">Holiday</span>
              <span className="rounded-1 badge bg-warning text-darker">
                {statusCounts?.HOLIDAY || 0}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center p-2 rounded-2 flex-fill flex-md-grow-0 text-bg-info-soft border border-info">
              <span className="fw-medium text-darker">Earned Leave</span>
              <span className="rounded-1 badge bg-info text-white">
                {statusCounts?.EARNED_LEAVE || 0}
              </span>
            </div>

            <div className="d-flex justify-content-between align-items-center p-2 rounded-2 flex-fill flex-md-grow-0 text-bg-success-soft border border-success">
              <span className="fw-medium text-darker">Paid Leave</span>
              <span className="rounded-1 badge bg-success text-white">
                {statusCounts?.PAID_LEAVE || 0}
              </span>
            </div>

            <div className="d-flex justify-content-between align-items-center p-2 rounded-2 flex-fill flex-md-grow-0 text-bg-warning-soft border border-warning">
              <span className="fw-medium text-darker">Unpaid Leave</span>
              <span className="rounded-1 badge bg-warning text-darker">
                {statusCounts?.UNPAID_LEAVE || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="col-xs-12 col-md-9 d-flex justify-content-center ">
          <div style={{ width: "85%" }}>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-1">
              <h6 className="fw-bold">Calendar</h6>
              <div className="d-flex gap-2">
                <div>
                  <GlobalSearchableDropdown
                    options={getMonthOptions(selectedYear?.value)}
                    selected={selectedMonth}
                    onSelect={(item) => setSelectedMonth(item)}
                    placeholder="Month"
                    isClearable={false}
                  />
                </div>
                <div>
                  <GlobalSearchableDropdown
                    options={getYearsOptions()}
                    selected={selectedYear}
                    onSelect={(item) => setSelectedYear(item)}
                    placeholder="Year"
                    isClearable={false}
                  />
                </div>
                <CustomTooltip title="Export">
                  <button
                    className="btn btn-success"
                    style={{ padding: "0.45rem 0.6rem" }}
                    disabled={exportLoading}
                    onClick={handleExport}
                  >
                    {exportLoading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaDownload size="15" />
                    )}
                  </button>
                </CustomTooltip>
              </div>
            </div>

            <div className="calendar-grid text-center">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="fw-semibold text-darker small">
                  {d}
                </div>
              ))}

              {emptyCells.map((_, i) => (
                <div key={`empty-${i}`} />
              ))}

              {daysInMonth.map((day) => {
                const status = getStatus(day);
                const isToday =
                  format(day, "yyyy-MM-dd") ===
                  format(new Date(), "yyyy-MM-dd");
                return (
                  <div
                    key={format(day, "yyyy-MM-dd")}
                    className={`text-darker day-cell fw-semibold rounded 
          ${status ? ` ${statusColors[status]}` : "bg-light"}
        `}
                  >
                    {isToday && <span className="today-dot bg-primary" />}
                    {format(day, "d")}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
